import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowUpFromBracket';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { ImportStatus } from '../../../../../../../../components/csv-import/data/types/import-status';
import { ImportType } from '../../../../../../../../components/csv-import/data/types/import-type';
import { StepType } from '../../../../../../../../components/csv-import/data/types/step-type';
import { ImportState } from '../../../../../../../../components/csv-import/ui/components/import-state';
import { SingleImportDrawer } from '../../../../../../../../components/csv-import/ui/components/single-import-drawer/single-import-drawer';
import { CreateMappingStepBody } from '../../../../../../../../components/csv-import/ui/components/steps/create-mapping-step/create-mapping-step';
import {
  ImportIsProcessingStepBody,
  ImportIsProcessingStepFooter,
} from '../../../../../../../../components/csv-import/ui/components/steps/import-is-processing-step/import-is-processing-step';
import { InventoryGridViewIds } from '../../../../../../../../constants/grid-view-ids';
import { useModal } from '../../../../../../../../hooks/use-modal';
import Global from '../../../../../../../../store/global-selectors';
import { Workflows } from '../../../../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../../../tracking/use-workflow-tracking';
import { ProductImportContext } from '../../../../context/product-import-context';
import { InitialImportStep } from '../initial-import-step';
import { ConfigStepFooter } from './config-step-footer';
import { InventoryTrackingSetup } from './inventory-tracking-setup';
import { MappingStepFooter } from './mapping-step-footer';
import { importMessages, messages } from './messages';
import styles from './styles.module.less';

export const IMPORT_STEPS = [
  {
    id: StepType.CREATE_CONFIG,
    title: <FormattedMessage {...importMessages.uploadFile} />,
    body: <InitialImportStep />,
    footer: <ConfigStepFooter />,
  },
  {
    id: StepType.CREATE_MAPPING,
    title: <FormattedMessage {...importMessages.matchFields} />,
    body: <CreateMappingStepBody />,
    footer: <MappingStepFooter />,
  },
  {
    id: StepType.IMPORT_IS_PROCESSING,
    title: <FormattedMessage {...importMessages.importResult} />,
    body: (
      <ImportIsProcessingStepBody
        successMessage={<FormattedMessage {...importMessages.importResultSuccess} />}
        title={<FormattedMessage {...importMessages.importResult} />}
      />
    ),
    footer: <ImportIsProcessingStepFooter />,
  },
];

/**
 * Component that displays the appropriate import state based on the status of the latest import.
 * @returns {JSX.Element} The rendered result import step component
 */
export const ResultImportStep = () => {
  const latestImport = useSelector((state) => Global.selectCsvImportType(state, ImportType.productWithInventory));

  const { open, isOpen, close } = useModal();

  const trackWorkflow = useTrackWorkflow(Workflows.CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_FLOW);

  const onOpen = () => {
    if (latestImport.status !== ImportStatus.ACTIVE) {
      trackWorkflow(WorkflowSteps.START, { version: 2 });
    }
    open();
  };

  const onCancel = (step) => {
    return () => {
      close();
      trackWorkflow(WorkflowSteps.CANCEL, { step });
    };
  };

  return (
    <>
      <div className={styles['result-content']} data-testid='result-import-step'>
        <Text className={styles['main-title']} textType={TextType.H2} value={messages.mainTitle} />
        <Text className={styles['title-description']} textType={TextType.Description} value={messages.titleDescription} />
        <ImportState importType={ImportType.productWithInventory} linkToSeeImportResult={InventoryGridViewIds.PATHNAME} open={onOpen} />
        <Button
          className={styles['import-btn']}
          disabled={latestImport.status === ImportStatus.ACTIVE}
          onClick={onOpen}
          theme={THEME_CONSTANTS.link}
        >
          <FormattedMessage {...messages.importAnotherFile} />
          <FontAwesomeIcon className={styles['import-icon']} icon={faArrowUpFromBracket} />
        </Button>
        <InventoryTrackingSetup />
      </div>
      {isOpen ? (
        <ProductImportContext close={close}>
          <SingleImportDrawer header={<FormattedMessage {...importMessages.importHeader} />} onCancel={onCancel} steps={IMPORT_STEPS} />
        </ProductImportContext>
      ) : null}
    </>
  );
};
